import React, { useContext, useEffect, useState } from 'react';
import API from '../../../services/interceptor';
import Dropdown from '../Comman/Dropdown';
import { AuthContext } from '../../../context/AuthContext';
import AddCategoryItemModal from '../Comman/AddCategoryItemModal';

const CATEGORY_OPTIONS = [
    'Dental Supplies',
    'Equipment Maintenance',
    'Staff Salary',
    'Rent',
    'Utilities',
    'Lab Fees',
    'Marketing',
    'Miscellaneous',
    'Other',
];

const PAYMENT_METHODS = ['Cash', 'Card', 'Bank Transfer', 'UPI', 'Cheque', 'Other'];
const APPROVAL_STATUS = ['Pending', 'Approved', 'Rejected'];

export default function ExpenseManager() {

    const { venderNames } = useContext(AuthContext);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("allergiesName");

    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadingFile, setUploadingFile] = useState(false);

    // Filters
    const [filterCategory, setFilterCategory] = useState('');
    const [filterApproval, setFilterApproval] = useState('');
    const [filterStart, setFilterStart] = useState('');
    const [filterEnd, setFilterEnd] = useState('');

    // Modal/form state
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(initialForm());

    useEffect(() => {
        fetchExpenses();
    }, []);

    async function fetchExpenses() {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (filterCategory) params.category = filterCategory;
            if (filterApproval) params.approvalStatus = filterApproval;
            if (filterStart) params.startDate = filterStart;
            if (filterEnd) params.endDate = filterEnd;

            const res = await API.get("/api/expenses", { params });
            setExpenses(res.data.expenses || res.data || []);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.details || err.message || 'Failed to fetch expenses');
        } finally {
            setLoading(false);
        }
    }

    function openCreate() {
        setEditing(null);
        setForm(initialForm());
        setShowForm(true);
    }

    function openEdit(exp) {
        setEditing(exp);
        setForm({
            date: exp.date ? exp.date.slice(0, 10) : '',
            category: exp.category || 'Miscellaneous',
            description: exp.description || '',
            amount: exp.amount ?? 0,
            paymentMethod: exp.paymentMethod || 'Cash',
            vendorName: exp.vendorName || '',
            invoiceNumber: exp.invoiceNumber || '',
            notes: exp.notes || '',
            approvalStatus: exp.approvalStatus || 'Pending',
            attachments: exp.attachments || [],
        });
        setShowForm(true);
    }

    function closeForm() {
        setShowForm(false);
        setEditing(null);
        setForm(initialForm());
    }

    async function handleFileUpload(e) {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingFile(true);
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", "mypreset");
        formData.append("cloud_name", "dsi1mb2aj");

        try {
            const response = await fetch(
                `https://api.cloudinary.com/v1_1/dsi1mb2aj/image/upload`,
                {
                    method: "POST",
                    body: formData,
                }
            );
            const data = await response.json();

            const newAttachment = {
                fileName: file.name,
                fileUrl: data.secure_url,
            };

            setForm(f => ({
                ...f,
                attachments: [...f.attachments, newAttachment]
            }));

            e.target.value = '';
        } catch (error) {
            console.error("Error uploading file:", error);
            setError("Failed to upload file. Please try again.");
        } finally {
            setUploadingFile(false);
        }
    }

    function removeAttachment(index) {
        setForm(f => ({
            ...f,
            attachments: f.attachments.filter((_, i) => i !== index)
        }));
    }

    async function submitForm(e) {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const payload = {
                date: form.date ? new Date(form.date).toISOString() : undefined,
                category: form.category,
                description: form.description,
                amount: Number(form.amount) || 0,
                paymentMethod: form.paymentMethod,
                vendorName: form.vendorName,
                invoiceNumber: form.invoiceNumber,
                notes: form.notes,
                approvalStatus: form.approvalStatus,
                attachments: form.attachments,
            };

            if (editing) {
                await API.put(`/api/expenses/${editing._id}`, payload);
            } else {
                await API.post("/api/expenses", payload);
            }

            await fetchExpenses();
            closeForm();
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.details || err.message || 'Failed to save expense');
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete(id) {
        if (!window.confirm('Delete this expense? This cannot be undone.')) return;
        setLoading(true);
        try {
            await API.delete(`/api/expenses/${id}`);
            await fetchExpenses();
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.details || err.message || 'Failed to delete');
        } finally {
            setLoading(false);
        }
    }

    async function handleApprove(id, status) {
        if (!['Approved', 'Rejected'].includes(status)) return;
        setLoading(true);
        try {
            await API.patch(`/api/expenses/${id}/approve`, { status });
            await fetchExpenses();
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.details || err.message || 'Failed to update approval');
        } finally {
            setLoading(false);
        }
    }

    function applyFilters() {
        fetchExpenses();
    }

    function resetFilters() {
        setFilterCategory('');
        setFilterApproval('');
        setFilterStart('');
        setFilterEnd('');
        setTimeout(() => fetchExpenses(), 0);
    }

    const totalExpenses = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
    const approvedExpenses = expenses.filter(e => e.approvalStatus === 'Approved').reduce((sum, exp) => sum + (exp.amount || 0), 0);
    const pendingExpenses = expenses.filter(e => e.approvalStatus === 'Pending').reduce((sum, exp) => sum + (exp.amount || 0), 0);

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Expense Management</h1>
                        <p className="text-sm text-gray-600 mt-1">Track and manage clinic expenses</p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={openCreate}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
                        >
                            + Add Expense
                        </button>
                        <button
                            onClick={fetchExpenses}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                        >
                            ↻ Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 font-medium">Total Expenses</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1">₹{totalExpenses.toLocaleString()}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <span className="text-blue-600 text-xl">💰</span>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 font-medium">Approved</p>
                            <p className="text-2xl font-bold text-green-600 mt-1">₹{approvedExpenses.toLocaleString()}</p>
                        </div>
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                            <span className="text-green-600 text-xl">✓</span>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 font-medium">Pending</p>
                            <p className="text-2xl font-bold text-yellow-600 mt-1">₹{pendingExpenses.toLocaleString()}</p>
                        </div>
                        <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                            <span className="text-yellow-600 text-xl">⏱</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-5 mb-6 border border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Filters</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="text-xs font-medium text-gray-700 block mb-1.5">Category</label>
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All Categories</option>
                            {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-gray-700 block mb-1.5">Approval Status</label>
                        <select
                            value={filterApproval}
                            onChange={(e) => setFilterApproval(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All Status</option>
                            {APPROVAL_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-gray-700 block mb-1.5">Start Date</label>
                        <input
                            type="date"
                            value={filterStart}
                            onChange={(e) => setFilterStart(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    <div>
                        <label className="text-xs font-medium text-gray-700 block mb-1.5">End Date</label>
                        <input
                            type="date"
                            value={filterEnd}
                            onChange={(e) => setFilterEnd(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                <div className="flex gap-2 mt-4">
                    <button
                        onClick={applyFilters}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                        Apply Filters
                    </button>
                    <button
                        onClick={resetFilters}
                        className="px-4 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    >
                        Reset
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                    {error}
                </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Date</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Category</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Amount</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Payment</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Vendor</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {expenses.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                                            No expenses found
                                        </td>
                                    </tr>
                                ) : expenses.map(exp => (
                                    <tr key={exp._id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                            {exp.date ? new Date(exp.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{exp.category}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{exp.description || '-'}</td>
                                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">₹{exp.amount?.toLocaleString?.() ?? exp.amount}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{exp.paymentMethod}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{exp.vendorName || '-'}</td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${exp.approvalStatus === 'Approved'
                                                ? 'bg-green-100 text-green-800'
                                                : exp.approvalStatus === 'Rejected'
                                                    ? 'bg-red-100 text-red-800'
                                                    : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                {exp.approvalStatus}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex gap-1">
                                                <button
                                                    onClick={() => openEdit(exp)}
                                                    className="px-2.5 py-1.5 bg-blue-600 text-white rounded-md text-xs hover:bg-blue-700 transition-colors font-medium"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(exp._id)}
                                                    className="px-2.5 py-1.5 bg-red-600 text-white rounded-md text-xs hover:bg-red-700 transition-colors font-medium"
                                                >
                                                    Delete
                                                </button>
                                                {exp.approvalStatus !== 'Approved' && (
                                                    <button
                                                        onClick={() => handleApprove(exp._id, 'Approved')}
                                                        className="px-2.5 py-1.5 bg-green-600 text-white rounded-md text-xs hover:bg-green-700 transition-colors font-medium"
                                                    >
                                                        Approve
                                                    </button>
                                                )}
                                                {exp.approvalStatus !== 'Rejected' && (
                                                    <button
                                                        onClick={() => handleApprove(exp._id, 'Rejected')}
                                                        className="px-2.5 py-1.5 bg-gray-600 text-white rounded-md text-xs hover:bg-gray-700 transition-colors font-medium"
                                                    >
                                                        Reject
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white w-full max-w-3xl rounded-lg shadow-xl my-8">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {editing ? 'Edit Expense' : 'Add New Expense'}
                            </h3>
                        </div>

                        <form onSubmit={submitForm} className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* Date */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        required
                                        value={form.date}
                                        onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                {/* Category */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Category <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        required
                                        value={form.category}
                                        onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>

                                {/* Amount */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Amount (₹) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        required
                                        value={form.amount}
                                        onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                {/* Payment Method */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Payment Method
                                    </label>
                                    <select
                                        value={form.paymentMethod}
                                        onChange={(e) => setForm(f => ({ ...f, paymentMethod: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>

                                {/* Vendor Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Vendor Name
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedCategory("venderNames");
                                                setIsOpen(!isOpen);
                                            }}
                                            className="ml-2 text-xs text-blue-600 underline"
                                        >add</button>
                                    </label>
                                    {/* <input
                                        type="text"
                                        value={form.vendorName}
                                        onChange={(e) => setForm(f => ({ ...f, vendorName: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        /> */}
                                    <Dropdown
                                        item={"vendorNames"} // Change from 'item' to 'selectedUser'
                                        selectedOption={form.vendorName}
                                        onChange={(e) => setForm(f => ({ ...f, vendorName: e.target.value }))}
                                        listOptions={venderNames}
                                        isRequired={true}
                                    />
                                    {console.log("venderNames", venderNames)}
                                </div>

                                {/* Invoice Number */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Invoice Number
                                    </label>
                                    <input
                                        type="text"
                                        value={form.invoiceNumber}
                                        onChange={(e) => setForm(f => ({ ...f, invoiceNumber: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                {/* Description */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Description
                                    </label>
                                    <input
                                        type="text"
                                        value={form.description}
                                        onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Brief description of the expense"
                                    />
                                </div>

                                {/* Notes */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Notes
                                    </label>
                                    <textarea
                                        value={form.notes}
                                        onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                                        rows={3}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Additional notes or remarks"
                                    />
                                </div>

                                {/* Approval Status */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Approval Status
                                    </label>
                                    <select
                                        value={form.approvalStatus}
                                        onChange={(e) => setForm(f => ({ ...f, approvalStatus: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        {APPROVAL_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>

                                {/* File Upload */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Attachments
                                    </label>
                                    <input
                                        type="file"
                                        accept="application/pdf,image/*"
                                        onChange={handleFileUpload}
                                        disabled={uploadingFile}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                                    />
                                    {uploadingFile && (
                                        <p className="text-xs text-blue-600 mt-2">Uploading file...</p>
                                    )}

                                    {/* Attachments List */}
                                    {form.attachments.length > 0 && (
                                        <div className="mt-3 space-y-2">
                                            {form.attachments.map((att, idx) => (
                                                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                                        <span className="text-blue-600">📎</span>
                                                        <a
                                                            href={att.fileUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-sm text-blue-600 hover:underline truncate"
                                                        >
                                                            {att.fileName}
                                                        </a>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeAttachment(idx)}
                                                        className="ml-2 text-red-600 hover:text-red-700 text-sm font-medium"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={closeForm}
                                    className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading || uploadingFile}
                                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm disabled:opacity-50"
                                >
                                    {loading ? 'Saving...' : editing ? 'Update Expense' : 'Create Expense'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <AddCategoryItemModal
                isOpen={isOpen}
                selectedCategory={selectedCategory}
                onClose={() => setIsOpen(false)}
                onAdded=""
            />
        </div>
    );
}

function initialForm() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return {
        date: `${yyyy}-${mm}-${dd}`,
        category: 'Miscellaneous',
        description: '',
        amount: 0,
        paymentMethod: 'Cash',
        vendorName: '',
        invoiceNumber: '',
        notes: '',
        approvalStatus: 'Pending',
        attachments: [],
    };
}